import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subscription, timer } from 'rxjs';
import { map, switchMap, takeUntil, takeWhile } from 'rxjs/operators';
import { Position } from '../interfaces/position.interface';
import { OAuthService } from './oauth.service';
import { MetaApiBrokerAdapterService } from './meta-api-broker-adapter.service';
import { MetaApiService } from './meta-api.service';
import { DatePipe } from '@angular/common';
import { SseService } from './SseService';
import { CookieService } from 'ngx-cookie-service';
import { BrokerTimeService } from './broker-time.service';

export interface AccountPositions {
  /** Cuenta a la que pertenecen las posiciones (ver PositionsService.accountKey); null = desconocida */
  accountKey: string | null;
  positions: Position[];
  ts: number;
}

@Injectable({
  providedIn: 'root'
})
export class PositionsService implements OnDestroy {
  private positionsSubject = new BehaviorSubject<AccountPositions>({ accountKey: null, positions: [], ts: 0 });
  /** Posiciones de la cuenta activa en esta pestaña o de la última difundida por otra pestaña, sin filtrar */
  public positions$ = this.positionsSubject.pipe(map(p => p.positions));
  public accountPositions$ = this.positionsSubject.asObservable();
  private currentAccountKey: string | null = null;
  private broadcastChannel: BroadcastChannel;
    public licencia: string;

  private subscription: Subscription | null = null;
  private invalidatecacheposition: Subscription | null = null;
  private readonly STORAGE_KEY = 'positions_data';
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  private readonly CHANNEL_NAME = 'positions-channel';

  constructor(private _sseservice: SseService,
    private oauthService: OAuthService,
    private metaApiAdapter: MetaApiBrokerAdapterService,
    private metaApiService: MetaApiService,
    private datePipe: DatePipe,
    public _cookieService: CookieService,
    private brokerTimeService: BrokerTimeService
  ) {
     this.licencia = this._cookieService.get('auth_token');
    this.initBroadcastChannel();
  }

  private initBroadcastChannel(): void {
    this.broadcastChannel = new BroadcastChannel(this.CHANNEL_NAME);
    this.broadcastChannel.onmessage = (event) => {
      if (event.data.type === 'positions-update') {
        this.positionsSubject.next({
          accountKey: event.data.accountKey ?? null,
          positions: event.data.positions,
          ts: event.data.ts ?? Date.now()
        });
      }
    };
  }

  /** Identifica la cuenta de broker; debe calcularse igual en /broker y en /broker-operations */
  static accountKey(tipoBroker: string | null | undefined, nombre: string, metaApiAccountId?: string | null): string {
    return tipoBroker === 'METAAPI'
      ? `METAAPI:${metaApiAccountId || nombre}`
      : `IBKR:${nombre}`;
  }

  /** Offset del broker en minutos → "+HHmm" que acepta DatePipe; undefined (hora del navegador) si no se conoce */
  private static offsetToDatePipeTz(offsetMin: number | null): string | undefined {
    if (offsetMin == null || !Number.isFinite(offsetMin)) return undefined;
    const abs = Math.abs(offsetMin);
    const hh = Math.floor(abs / 60).toString().padStart(2, '0');
    const mm = (abs % 60).toString().padStart(2, '0');
    return `${offsetMin < 0 ? '-' : '+'}${hh}${mm}`;
  }

  /** MetaAPI token and app licence are both required; without them the API answers 401 */
  private hasMetaApiSession(): boolean {
    return !!this.metaApiService.getToken() && !!this._cookieService.get('auth_token');
  }

  public startPositionsUpdateMetaApi(accountId: string, tipoBroker: string): void {
    this.stopPositionsUpdate();
    this.currentAccountKey = PositionsService.accountKey('METAAPI', accountId, accountId);
    if (!this.hasMetaApiSession()) return;
    this.loadCachedFor(this.currentAccountKey);

    // fechaHoraActual en hora del broker (la misma que "HORA EXCHANGE"), no la del navegador
    let brokerTz: string | undefined;
    this.subscription = this.brokerTimeService.getOffsetMinutes(accountId).pipe(
      switchMap(offsetMin => {
        brokerTz = PositionsService.offsetToDatePipeTz(offsetMin);
        return timer(0, 5000);
      }),
      // Logout from another tab only clears localStorage/cookies: stop on the next tick
      takeWhile(() => this.hasMetaApiSession()),
      // Logout in this tab: stop immediately, cancelling the in-flight request so a late 401
      // doesn't trigger the token auto-refresh and store the credentials again
      takeUntil(this.metaApiService.credentialsCleared$),
      switchMap(() => this.metaApiAdapter.getPositions(accountId))
    ).subscribe({
      next: (rawPositions: any[]) => {
        const now = this.datePipe.transform(new Date(), 'dd-MM-yyyy HH:mm', brokerTz) || '';
        
        const active = rawPositions.filter(p => (p.quantity ?? 0) !== 0);
        
          // IBKR-shaped payload — keeps broker/positions contract intact
          const formatted: Position[] = active.map(p => ({
            contractDesc: p.symbol ?? '',
            avgPrice:     Number(p.avgCost ?? 0),
            currency:     p.raw?.currency ?? '',
            position:     Number(p.quantity ?? 0),
            coste:        Number((p.avgCost ?? 0) * (p.quantity ?? 0)),
            mktPrice:     Number(p.marketPrice ?? 0),
            valorActual:  Number(p.marketValue ?? 0),
            beneficio:    Number(p.unrealizedPnl ?? 0),
            fechaHoraActual: now,
            conid:        p.conid,
            assetClass:   'FX'
          }));
          this.updatePositions(formatted);
          //this._sseservice.apiPostNew('broker/positions', formatted, this.licencia).subscribe();
          //this.updatePositions(metaApiPayload as unknown as Position[]); // Cast to Position[] for broadcasting

      // }
        const licencia = this._cookieService.get('auth_token');
        if (tipoBroker === 'METAAPI' && licencia) {
          // MetaAPI-specific payload with native BrokerPosition fields
          const metaApiPayload = active.map(p => ({
            symbol:        p.symbol ?? '',
            conid:         p.conid,
            quantity:      Number(p.quantity ?? 0),
            avgCost:       Number(p.avgCost ?? 0),
            marketPrice:   Number(p.marketPrice ?? 0),
            marketValue:   Number(p.marketValue ?? 0),
            unrealizedPnl: Number(p.unrealizedPnl ?? 0),
            realizedPnl:   Number(p.realizedPnl ?? 0),
            currency:      p.raw?.currency ?? '',
            fechaHoraActual: now
          }));
          //crear formated
          this._sseservice.apiPostNew('metaapi/positions/'+accountId, metaApiPayload, licencia).subscribe();
        } 
      },
      error: (err) => console.error('[PositionsService MetaAPI] error', err)
    });
  }

  public startPositionsUpdate(nombre: string): void {
    // Clear existing subscription
    this.stopPositionsUpdate();
    this.currentAccountKey = PositionsService.accountKey('IBKR', nombre);

    // Load cached data initially
    this.loadCachedFor(this.currentAccountKey);
   
     this.subscription = this.oauthService
      .fetchDataEverySecoundsbrk(`/portfolio2/${nombre}/positions`, 5000)
      .subscribe({
        next: (data: any[]) => {
          
          const formattedPositions: Position[] = this.formatPositionsfromposition2fromposition(data);
          this.updatePositions(formattedPositions);
          this._sseservice.apiPostNew('broker/positions', formattedPositions, this.licencia).subscribe({
            next: (response) => {
              //console.log('Positions sent to backend successfully:', response);
            },
            error: (error) => {
              console.error('Error sending positions to backend:', error);
            }
          });
         // const formattedPositions = this.formatPositions(data);
         // this.updatePositions(formattedPositions);
        },
        error: (error) => console.error('Error fetching positions:', error)
      });
  }
  

  public stopPositionsUpdate(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
    }
  }

  private formatPositions(data: any[]): Position[] {
    return data
      .map(position => ({
        ...position,
        avgPrice: Number(position.avgPrice).toFixed(2),
        coste: (position.avgPrice * position.position).toFixed(2),
        valorActual: (position.position * position.mktPrice).toFixed(2),
        beneficio: ((position.position * position.mktPrice) - 
                   (position.avgPrice * position.position)).toFixed(2),
        fechaHoraActual: this.datePipe.transform(new Date(), 'dd-MM-yyyy HH:mm') || ''
      }))
      .filter(position => position.position !== 0);
  }

  formatPositionsfromposition2fromposition(data: any[]) {
   return data
      .map(position => ({
        ...position,
                contractDesc: position.description,
        mktPrice: position.marketPrice,
        avgPrice: Number(position.avgPrice).toFixed(2),
        coste: (position.avgPrice * position.position).toFixed(2),
        valorActual: (position.position * position.marketPrice).toFixed(2),
        beneficio: ((position.position * position.marketPrice) - 
                   (position.avgPrice * position.position)).toFixed(2),
        fechaHoraActual: this.datePipe.transform(new Date(), 'dd-MM-yyyy HH:mm') || ''
      }))
      .filter(position => position.position !== 0);
  }

  private updatePositions(positions: Position[]): void {
    const accountKey = this.currentAccountKey;
    const ts = Date.now();
    this.positionsSubject.next({ accountKey, positions, ts });
    this.saveToCache(accountKey, positions, ts);
    // Broadcast positions to other tabs
    this.broadcastChannel.postMessage({
      type: 'positions-update',
      accountKey,
      positions,
      ts
    });
  }

  private saveToCache(accountKey: string | null, positions: Position[], ts: number): void {
    const cacheData = {
      timestamp: ts,
      accountKey,
      data: positions
    };
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(cacheData));
  }

  /** Emite la caché solo si pertenece a esa cuenta y no ha caducado (una caché sin accountKey no se usa) */
  public loadCachedFor(accountKey: string): void {
    const cached = localStorage.getItem(this.STORAGE_KEY);
    if (!cached) return;
    try {
      const { timestamp, accountKey: cachedKey, data } = JSON.parse(cached);
      if (cachedKey === accountKey && Date.now() - timestamp < this.CACHE_DURATION) {
        this.positionsSubject.next({ accountKey, positions: data, ts: timestamp });
      }
    } catch {
      // caché corrupta: se ignora
    }
  }

  ngOnDestroy() {
    this.stopPositionsUpdate();
    this.broadcastChannel.close();
  }
}